<template>
  <div class="france-map">
    <svg x="0px" y="0px" width="100%" id="map-svg"
         :viewBox="`0 0 ${viewBoxSize} ${viewBoxSize}`"
         xmlns="http://www.w3.org/2000/svg"
         class="map-container"
         @click.self="selectedRegion = null">
      <g class="map-region"
         :style="style.mapRegion"
         transform="matrix(0.99999629,0,0,1.002717,38.975013,-4.0214358)">
        <g v-for="(a, b) in sortedDisplayData" :key="`group-${b}-${a.key}`"
           :class="{ 'selected-region': selectedRegion === a }">
          <path v-for="(c, d) in a.d"
                class="cursor-pointer"
                @click.stop="selectedRegion = a"
                v-if="a.d != null"
                :key="`path-${d}-${a.key}`"
                :d="c"
                :fill="regionsColors[a.key].hex8">
          </path>
          <rect v-if="a.square != null"
                :key="`square-${b}-${a.key}`"
                class="map-square"
                :style="style.mapSquare"
                :width="a.square.w"
                :height="a.square.h"
                :x="a.square.x"
                :y="a.square.y">
          </rect>
          <g v-if="displayPellets[a.key]" @click="selectedRegion = a">
            <rect v-if="isPelletsStyle.incidence || isPelletsStyle.incidenceDouble"
                  :key="`incidence-pellet-${b}-${a.key}`"
                  :x="a.pelletPosition.x - 24 + incidencePelletsSizing[a.key].adjust.x"
                  :y="a.pelletPosition.y - 21 + incidencePelletsSizing[a.key].adjust.y"
                  :fill="pelletsColors[a.key].hex8"
                  stroke-width="0"
                  :width="incidencePelletsSizing[a.key].size.w"
                  :height="incidencePelletsSizing[a.key].size.h"
                  rx="8" />
            <circle v-else
                    :key="`participation-pellet-${b}-${a.key}`"
                    :cx="a.pelletPosition.x"
                    :cy="a.pelletPosition.y - 6"
                    :r="displayOptions.roundPelletDiameter"
                    :fill="pelletsColors[a.key].hex8"
                    stroke-width="0">
            </circle>
            <text v-if="mapData[a.key] !== ''"
                  :key="`value-1-${b}-${a.key}`"
                  class="pellet-text"
                  :style="`font-size:${fontSize.pellets}px;${style.pelletText}`"
                  :x="a.pelletPosition.x"
                  :y="a.pelletPosition.y + adjustPositionValue1[a.key]">
              {{ displayedValues[a.key] }}<tspan v-if="displayOptions.addPercent.value1" :style="`font-size:${fontSize.pellets * sc.percentScale}px;${style.pelletText}`" >%</tspan>
            </text>
            <text v-if="mapData[a.key] !== '' &&
                        isPelletsStyle.twoValues &&
                        displayedValues2[a.key]"
                  :key="`value-2-${b}-${a.key}`"
                  class="pellet-text"
                  :style="`font-size:${fontSize.pellets * sc.value2Scale}px;${style.pelletText}`"
                  :x="a.pelletPosition.x"
                  :y="a.pelletPosition.y + adjustPositionValue2">
              {{ displayedValues2[a.key] }}<tspan v-if="displayOptions.addPercent.value2" :style="`font-size:${fontSize.pellets * sc.value2Scale * sc.percentScale}px;${style.pelletText}`" >%</tspan>
            </text>
          </g>
          <text v-if="a.name.forceDisplay"
                :key="`region-name-${b}-${a.key}`"
                class="region-name-text"
                :style="`vertical-align:center;font-size:${fontSize.regions}px;${style.regionNameText}`"
                :x="a.name.position.x"
                :y="a.name.position.y">
            {{a.name.text}}
          </text>
        </g>
      </g>
    </svg>
  </div>

</template>

<script>
import franceMapData from '@/sc/francemap'
import impl from '@/views/tools/impl/map'

import assert from '@/sui/tools/assert'

const regionList = franceMapData.regionsList()

function getRegionListWithDefaultValue (value) {
  const result = {}
  for (let i = 0; i < regionList.length; i++) {
    const regionKey = regionList[i].key
    result[regionKey] = value
  }
  return result
}

function getDisplayedMapValues (
  regionsList,
  mapValues,
  decimalCount,
  percentValue
) {
  const result = {}
  if (regionsList == null || regionsList.length === 0 ||
      mapValues == null || mapValues.length === 0) {
    return result
  }

  for (let i = 0; i < regionsList.length; i++) {
    const regionKey = regionsList[i].key
    const regionValue = mapValues[regionKey]

    if (regionValue == null || regionValue === '') {
      result[regionKey] = ''
      continue
    }
    const floatValue = parseFloat(regionValue)

    if (percentValue != null) {
      const divisor = Number(percentValue)
      if (divisor !== 0) {
        const percentValue = floatValue / divisor * 100
        result[regionKey] = Math.round(percentValue)
        continue
      }
    }

    if (floatValue === 0) {
      result[regionKey] = '0'
      continue
    }

    const fixedValue = floatValue.toFixed(decimalCount)
    result[regionKey] = `${fixedValue}`.replace('.', ',')
  }
  return result
}

export default {
  name: 'france-map',

  props: {
    mapData: {
      type: Object,
      required: true
    },

    mapData2: {
      type: Object,
      required: true
    },

    displayPellets: {
      type: Object,
      default: function () {
        return getRegionListWithDefaultValue(false)
      }
    },

    pelletsColors: {
      type: Object,
      default: function () {
        return getRegionListWithDefaultValue(franceMapData.defaultPelletBgColor)
      }
    },

    regionsColors: {
      type: Object,
      default: function () {
        return getRegionListWithDefaultValue(franceMapData.defaultRegionBgColor)
      }
    },

    displayOptions: {
      type: Object,
      default: function () {
        return franceMapData.defaultMapDisplayOptions()
      }
    }
  },

  data: function () {
    return {
      selectedRegion: null,
      viewBoxSize: franceMapData.viewBoxSize,
      displayData: franceMapData.regionsData,
      style: franceMapData.styles,
      sc: {
        percentScale: 0.7,
        value2Scale: 0.8
      }
    }
  },

  watch: {
    selectedRegion: function (v) {
      if (v != null) {
        this.$emit('input', {
          key: v.key,
          name: v.name.text
        })
      } else {
        this.$emit('input', null)
      }
    }
  },

  computed: {
    sortedDisplayData: function () {
      const result = []
      for (let i = 0; i !== this.displayData.length; ++i) {
        const data = this.displayData[i]
        if (data === this.selectedRegion) {
          continue
        }
        result.push(data)
      }
      if (this.selectedRegion != null) {
        result.push(this.selectedRegion)
      }
      return result
    },

    displayedValues: function () {
      const opt = this.displayOptions
      return getDisplayedMapValues(
        this.displayData,
        this.mapData,
        opt.decimal.value1,
        opt.percentValue
      )
    },

    displayedValues2: function () {
      if (!this.isPelletsStyle.twoValues) {
        return {}
      }
      const opt = this.displayOptions
      return getDisplayedMapValues(
        this.displayData,
        this.mapData2,
        opt.decimal.value2,
        false,
        null
      )
    },

    adjustPositionValue1: function () {
      const result = {}
      const adjustFor = {
        oneValue: -4 + this.fontSize.pellets * 0.25,
        twoValues: -6
      }

      for (let i = 0; i < regionList.length; i++) {
        const regionKey = regionList[i].key
        const value2 = this.displayedValues2[regionKey]
        if (this.isPelletsStyle.twoValues &&
            value2 != null && value2 !== '') {
          result[regionKey] = adjustFor.twoValues
        } else {
          result[regionKey] = adjustFor.oneValue
        }
      }
      return result
    },

    adjustPositionValue2: function () {
      return this.fontSize.pellets * this.sc.value2Scale - 5
    },

    fontSize: function () {
      function getPxValue (v, def) {
        if (v == null || v === 0 || v === '') {
          return def
        }
        return v
      }

      const o = this.displayOptions
      return {
        pellets: getPxValue(o.pelletsFontSize, 14),
        regions: getPxValue(o.regionsFontSize, 11)
      }
    },

    isPelletsStyle: function () {
      return impl.askPelletsStyle(this.displayOptions.pelletsStyle)
    },

    incidencePelletsSizing: function () {
      const isStyle = this.isPelletsStyle
      assert(isStyle.incidence || isStyle.incidenceDouble)

      const result = {}
      if (isStyle.incidence) {
        for (let i = 0; i !== regionList.length; i++) {
          const key = regionList[i].key
          result[key] = {
            size: { w: '48', h: '30' },
            adjust: { x: 0, y: 0 }
          }
        }
        return result
      }

      const values = {
        one: {
          size: { w: '65', h: '35' },
          adjust: { x: -9, y: -3 }
        },

        two: {
          size: { w: '65', h: '45' },
          adjust: { x: -9, y: -7 }
        }
      }

      for (let i = 0; i !== regionList.length; i++) {
        const key = regionList[i].key
        const value2 = this.displayedValues2[key]
        const hasTwoEntries = value2 != null && value2 !== ''
        result[key] = hasTwoEntries ? values.two : values.one
      }

      return result
    }
  }
}
</script>
<style scoped>
.map-container {

  cursor: pointer;

  .map-region {
    opacity: 1;
    stroke-width: 1.5;
    stroke-miterlimit: 4;
    stroke: rgb(59, 59, 59);
    cursor: pointer;

    g {
      position: relative;
      z-index: 0;
    }

    path:hover {
      filter: sepia(0.4);
    }

    .selected-region {
      path {
        stroke: rgba(20, 20, 20);
        stroke-width: 2;
      }
      z-index: 1000;
      position: relative;
    }

    .map-square {
      opacity: 1;
      fill: none;
      stroke: rgb(59, 59, 59);
      stroke-width: 1.5;
    }

    .region-name-text {
      opacity: 1;
      stroke-width: 0.2;
      text-anchor: middle;
      font-family: serif;
    }

    .pellet-text {
      fill: white;
      stroke-width: 0;
      font-weight: bold;
      text-anchor: middle;
      font-family: serif;
      opacity: 1;
    }
  }
}
</style>
